import { Approval_Sort } from "../../../../generated/graphql";
import type { ListOfApprovalTemporaryStorageEntity } from "../../../../generated/graphql";
import type { ApprovalApplicationPayload } from "../../../../api/work-policy-application-api.types";

const STORAGE_KEY = "asonic.work-policy-application-drafts.v1";

interface StoredWorkPolicyApplicationDraft {
  employeeId: string;
  approvalRequestIdx: number;
  approvalType: Approval_Sort;
  savedAt: string;
  approval: ApprovalApplicationPayload;
}

export type WorkPolicyApplicationDraftListItem =
  ListOfApprovalTemporaryStorageEntity & {
    approval: ApprovalApplicationPayload;
  };

interface UpsertWorkPolicyApplicationDraftInput {
  employeeId: string;
  approvalRequestIdx: number;
  approvalType: Approval_Sort;
  approval: ApprovalApplicationPayload;
}

interface WorkPolicyLegacyCommonFields {
  extendSDate?: string | null;
  extendEDate?: string | null;
  extendSDateTime?: string | null;
  extendEDateTime?: string | null;
  approvalTitle?: string | null;
  temporaryExtendDescription?: string | null;
  extendDescription?: string | null;
  workingTemplateIdx?: number | null;
}

export interface WorkPolicyDraftCommonFields {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  title: string;
  description: string;
  workingTemplateIdx: number;
}

export function resolveWorkPolicyDraftCommonFields(
  information: WorkPolicyLegacyCommonFields,
  indexedApproval?: ApprovalApplicationPayload
): WorkPolicyDraftCommonFields {
  if (indexedApproval) {
    return {
      startDate: indexedApproval.startDate,
      endDate: indexedApproval.endDate,
      startTime: indexedApproval.startTime,
      endTime: indexedApproval.endTime,
      title: indexedApproval.title,
      description: indexedApproval.description ?? "",
      workingTemplateIdx: indexedApproval.workingTemplateIdx
    };
  }

  return {
    startDate: information.extendSDate ?? "",
    endDate: information.extendEDate ?? "",
    startTime: information.extendSDateTime ?? "",
    endTime: information.extendEDateTime ?? "",
    title: information.approvalTitle ?? "",
    description:
      information.temporaryExtendDescription ??
      information.extendDescription ??
      "",
    workingTemplateIdx: information.workingTemplateIdx ?? 0
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function read(storage: Storage): StoredWorkPolicyApplicationDraft[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is StoredWorkPolicyApplicationDraft => {
      if (!isRecord(item) || !isRecord(item.approval)) return false;
      return (
        typeof item.employeeId === "string" &&
        Number.isInteger(item.approvalRequestIdx) &&
        (item.approvalType === Approval_Sort.ApprovalLine ||
          item.approvalType === Approval_Sort.AutoApproval) &&
        typeof item.savedAt === "string" &&
        typeof item.approval.title === "string" &&
        Number.isInteger(item.approval.formTemplateIdx)
      );
    });
  } catch {
    return [];
  }
}

function write(
  storage: Storage,
  drafts: readonly StoredWorkPolicyApplicationDraft[]
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // The REST draft remains durable even when this browser cannot retain its index.
  }
}

function toListItem(
  item: StoredWorkPolicyApplicationDraft
): WorkPolicyApplicationDraftListItem {
  const { approval } = item;
  return {
    approvalRequestIdx: item.approvalRequestIdx,
    formTemplateIdx: approval.formTemplateIdx,
    approvalType: item.approvalType,
    signUpDatetime: item.savedAt,
    approvalTitle: approval.title,
    categoryId: 0,
    formIdx: 12,
    extendStartDate: approval.startDate,
    extendEndDate: approval.endDate,
    extendStartDateTime: approval.startTime,
    extendEndDateTime: approval.endTime,
    name: item.employeeId,
    attachedFileName:
      approval.fileNames.length > 0 ? approval.fileNames.join(", ") : null,
    attachedFileLink: null,
    approval
  };
}

export function listWorkPolicyApplicationDrafts(
  storage: Storage,
  employeeId: string
): WorkPolicyApplicationDraftListItem[] {
  return read(storage)
    .filter(item => item.employeeId === employeeId)
    .sort((left, right) => right.savedAt.localeCompare(left.savedAt))
    .map(toListItem);
}

export function upsertWorkPolicyApplicationDraft(
  storage: Storage,
  input: UpsertWorkPolicyApplicationDraftInput
): WorkPolicyApplicationDraftListItem {
  const drafts = read(storage).filter(
    item =>
      item.employeeId !== input.employeeId ||
      item.approvalRequestIdx !== input.approvalRequestIdx
  );
  const stored = { ...input, savedAt: new Date().toISOString() };
  drafts.push(stored);
  write(storage, drafts);
  return toListItem(stored);
}

export function removeWorkPolicyApplicationDraft(
  storage: Storage,
  employeeId: string,
  approvalRequestIdx: number
): void {
  write(
    storage,
    read(storage).filter(
      item =>
        item.employeeId !== employeeId ||
        item.approvalRequestIdx !== approvalRequestIdx
    )
  );
}
